/**
 * /onboarding/:token — pagina principale del flusso.
 *
 * 1. Mount: chiama validateOnboardingToken (CF NO AUTH).
 * 2. In base al risultato:
 *    - success: passa controllo a <OnboardingForm/> con prefilled
 *      data (4 step, consensi dal registro).
 *    - error (qualunque tipo: expired, used, not_found): redirect a
 *      /onboarding/expired senza specificare il motivo.
 *    - rate_limited: stesso redirect, messaggio generico.
 *
 * UX:
 *  - Stato loading con spinner accessibile fino a risposta CF.
 *  - Niente leak di info (nessun "token X non trovato in DB").
 */

import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { pazienteFunctions } from '../firebase-paziente';
import { OnboardingShell } from '../components/OnboardingShell';
import { OnboardingForm } from '../components/OnboardingForm';

interface ValidateTokenResponse {
  ok: true;
  pazienteId: string;
  prefilledData: {
    nome: string;
    cognome: string;
    email: string;
    telefono: string;
    sesso?: string;
  };
}

type ViewState =
  | { status: 'loading' }
  | { status: 'ok'; data: ValidateTokenResponse }
  | { status: 'error' };

export function OnboardingPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ViewState>(
    token ? { status: 'loading' } : { status: 'error' }
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<
          { token: string },
          ValidateTokenResponse
        >(pazienteFunctions, 'validateOnboardingToken');
        const res = await fn({ token });
        if (cancelled) return;
        setState({ status: 'ok', data: res.data });
      } catch {
        if (cancelled) return;
        setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <OnboardingShell>
        <div
          className="flex flex-col items-center justify-center py-16"
          role="status"
          aria-live="polite"
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-text-secondary">
            Verifichiamo il tuo link...
          </p>
        </div>
      </OnboardingShell>
    );
  }

  if (state.status === 'error') {
    return <Navigate to="/onboarding/expired" replace />;
  }

  return (
    <OnboardingForm
      token={token!}
      pazienteId={state.data.pazienteId}
      prefilled={state.data.prefilledData}
    />
  );
}
